/**
 * Crew messages data (features.crewMessages) and set-up photos. Crew replies, running late, notices and photo
 * uploads go through the API's /crew; the office reads replies (on allocations), running late messages and photos
 * under RLS, and opens a photo through /admin.
 */
import { adminBlob } from "../../lib/api";
import { crewBlob, crewFetch } from "../crew-api";
import { BRAND } from "../../lib/config";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

export const crewMessagesInUse = () => Boolean(BRAND.crewMessages);
const previewOnly = (): never => {
  throw new Error("Not available in preview mode.");
};
const check = <T>(r: { data: T | null; error: { message: string } | null }): T => {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
};

export type Reply = "accepted" | "declined";
export interface ShiftReply { response: Reply | null; respondedAt: string | null; declineReason: string | null }
export interface CrewNotice { id: string; kind: string; message: string; bookingId: string | null; createdAt: string }
export interface SetupPhoto { id: string; caption: string | null; crewName: string | null; createdAt: string; contentType: string }
export interface LateSent { text: { body: string; to: { label: string; phone: string }[] } }

/* ---------- crew ---------- */

export const replyToShift = (bookingId: string, response: Reply, reason?: string) =>
  PREVIEW ? previewOnly() : crewFetch<{ reply: ShiftReply }>(`/shifts/${bookingId}/reply`, { method: "POST", body: JSON.stringify({ response, reason: reason ?? null }) });
export const runningLate = (bookingId: string, minutes: number, note: string) =>
  PREVIEW ? previewOnly() : crewFetch<LateSent>(`/shifts/${bookingId}/late`, { method: "POST", body: JSON.stringify({ minutes, note: note.trim() || null }) });
export const myNotices = () => (PREVIEW || !crewMessagesInUse() ? Promise.resolve({ notices: [] as CrewNotice[] }) : crewFetch<{ notices: CrewNotice[] }>("/notices"));
export const ackNotices = (id?: string) => crewFetch<{ acknowledged: number }>("/notices/ack", { method: "POST", body: JSON.stringify({ id: id ?? null }) });

export const shiftPhotos = (bookingId: string) =>
  PREVIEW ? Promise.resolve({ photos: [] as SetupPhoto[], max: 10, canAdd: false }) : crewFetch<{ photos: SetupPhoto[]; max: number; canAdd: boolean }>(`/shifts/${bookingId}/photos`);
export function addShiftPhoto(bookingId: string, file: File, caption: string) {
  const f = new FormData();
  f.set("file", file);
  if (caption.trim()) f.set("caption", caption.trim());
  return crewFetch<{ photo: SetupPhoto }>(`/shifts/${bookingId}/photos`, { method: "POST", body: f }, { timeoutMs: 120_000 });
}
export const shiftPhotoBlob = (bookingId: string, id: string) => crewBlob(`/shifts/${bookingId}/photos/${id}`);

/** A Messages app link with the text filled in (iOS uses "&body=", others "?body="). */
export const smsHref = (to: string, body: string) => `sms:${to.replace(/\s/g, "")}${/iPhone|iPad|Mac/.test(navigator.userAgent) ? "&" : "?"}body=${encodeURIComponent(body)}`;

/* ---------- office ---------- */

export interface LateRow { id: string; booking_id: string; minutes: number | null; note: string | null; created_at: string; crew_members: { full_name: string } | null }
export interface PhotoRow { id: string; booking_id: string; caption: string | null; created_at: string; crew_members: { full_name: string } | null }

export async function shiftMessages(bookingIds: string[]): Promise<{ lates: LateRow[]; photos: PhotoRow[] }> {
  if (PREVIEW || !bookingIds.length) return { lates: [], photos: [] };
  const [lates, photos] = await Promise.all([
    crewMessagesInUse() ? supabase.from("shift_messages").select("id, booking_id, minutes, note, created_at, crew_members(full_name)").in("booking_id", bookingIds).order("created_at") : Promise.resolve({ data: [], error: null }),
    supabase.from("shift_photos").select("id, booking_id, caption, created_at, crew_members(full_name)").in("booking_id", bookingIds).order("created_at"),
  ]);
  return { lates: check(lates) as unknown as LateRow[], photos: check(photos) as unknown as PhotoRow[] };
}
export const officePhotoBlob = (id: string) => adminBlob(`/shift-photos/${id}/file`);

export interface ReplyRow {
  bookingId: string; jobId: string | null; reference: string | null; title: string | null; startsAt: string;
  declined: { name: string; reason: string | null }[]; waiting: string[];
}

/** Shifts in the next 3 days with a decline or someone not confirmed yet (for Today). */
export async function repliesToChase(now = new Date()): Promise<ReplyRow[]> {
  if (PREVIEW || !crewMessagesInUse()) return [];
  const to = new Date(now.getTime() + 3 * 86400_000).toISOString();
  const rows = check(
    await supabase
      .from("bookings")
      .select("id, job_id, starts_at, jobs(reference, title), allocations(response, decline_reason, crew_members(full_name))")
      .neq("status", "cancelled")
      .gte("starts_at", now.toISOString())
      .lte("starts_at", to)
      .order("starts_at"),
  ) as unknown as { id: string; job_id: string | null; starts_at: string; jobs: { reference: string; title: string } | null; allocations: { response: string | null; decline_reason: string | null; crew_members: { full_name: string } | null }[] }[];
  return rows
    .map((b) => ({
      bookingId: b.id, jobId: b.job_id, reference: b.jobs?.reference ?? null, title: b.jobs?.title ?? null, startsAt: b.starts_at,
      declined: b.allocations.filter((a) => a.response === "declined").map((a) => ({ name: a.crew_members?.full_name ?? "Crew member", reason: a.decline_reason })),
      waiting: b.allocations.filter((a) => !a.response).map((a) => a.crew_members?.full_name ?? "Crew member"),
    }))
    .filter((r) => r.declined.length || r.waiting.length);
}
